import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/* Copy dos WIDGETS de métricas (ADR-0353/0359): card genérico de gráfico, cards de KPI e as 4
   abas (receita/assinantes/cadastros/moderação). O header/seletor de moeda/refresh da PÁGINA
   ficam em `content/sections/metrics-dashboard.ts` (frente de routes/) — este arquivo é só o que
   `components/metrics/` desenha por conta própria. */
export interface MetricsWidgetsContent {
  readonly chartCard: {
    readonly emptyFilteredMessage: string;
    readonly clearFilters: string;
    readonly emptyMessage: string;
  };
  readonly customersByPlanChart: {
    readonly title: string;
    readonly tooltipCustomers: string;
  };
  readonly revenueByPlanChart: {
    readonly title: string;
  };
  readonly signupsTimeseriesChart: {
    readonly title: string;
    readonly tooltipSignups: string;
  };
  readonly metricCards: {
    readonly mrr: string;
    readonly mrrHint: (currency: string) => string;
    readonly churn30d: string;
    readonly activeUsers24h: string;
    readonly activeDevices24h: string;
    readonly blockedOrgs: string;
    readonly blockedOrgsHint: (total: string) => string;
    readonly signups7d: string;
    readonly signups30d: string;
  };
  readonly moderationTab: {
    readonly queueDepth: string;
    readonly decisionP50: string;
    readonly decisionP50Hint: string;
    readonly decisionP95: string;
    readonly decisionP95Hint: (target: number) => string;
    readonly aboveSla: (target: number) => string;
    readonly aboveSlaHint: string;
    readonly approved: string;
    readonly rejected: string;
    readonly fastpath: string;
    readonly autoRejectCap: string;
  };
  readonly planFilter: {
    readonly label: string;
    readonly resultCount: (filtered: number, total: number) => string;
  };
  readonly signupsFilters: {
    readonly periodAriaLabel: string;
    readonly grainAriaLabel: string;
    readonly preset7d: string;
    readonly preset30d: string;
    readonly preset90d: string;
    readonly preset1y: string;
    readonly grainDay: string;
    readonly grainWeek: string;
    readonly grainMonth: string;
  };
}

const METRICS_WIDGETS_CONTENT: Bilingual<MetricsWidgetsContent> = {
  'pt-BR': {
    chartCard: {
      emptyFilteredMessage: 'Nenhum plano corresponde aos filtros.',
      clearFilters: 'Limpar filtros',
      emptyMessage: 'Sem dados no período.',
    },
    customersByPlanChart: { title: 'Clientes por plano', tooltipCustomers: 'Clientes' },
    revenueByPlanChart: { title: 'Receita por plano' },
    signupsTimeseriesChart: { title: 'Signups no período', tooltipSignups: 'Signups' },
    metricCards: {
      mrr: 'MRR',
      mrrHint: (currency) => `moeda ${currency}`,
      churn30d: 'Churn 30d',
      activeUsers24h: 'Usuários ativos 24h',
      activeDevices24h: 'Devices ativos 24h',
      blockedOrgs: 'Empresas bloqueadas',
      blockedOrgsHint: (total) => `de ${total}`,
      signups7d: 'Signups 7d',
      signups30d: 'Signups 30d',
    },
    moderationTab: {
      queueDepth: 'Fila pendente',
      decisionP50: 'Decisão p50',
      decisionP50Hint: 'mediana',
      decisionP95: 'Decisão p95',
      decisionP95Hint: (target) => `vs alvo ${target}h`,
      aboveSla: (target) => `Acima do SLA (${target}h)`,
      aboveSlaHint: 'pendências + decisões',
      approved: 'Aprovadas',
      rejected: 'Rejeitadas',
      fastpath: 'Fastpath',
      autoRejectCap: 'Auto-reject (cap)',
    },
    planFilter: {
      label: 'Plano',
      resultCount: (filtered, total) => `${filtered} de ${total} planos`,
    },
    signupsFilters: {
      periodAriaLabel: 'Período',
      grainAriaLabel: 'Granularidade',
      preset7d: '7 dias',
      preset30d: '30 dias',
      preset90d: '90 dias',
      preset1y: '1 ano',
      grainDay: 'Diário',
      grainWeek: 'Semanal',
      grainMonth: 'Mensal',
    },
  },
  'en-US': {
    chartCard: {
      emptyFilteredMessage: 'No plan matches the filters.',
      clearFilters: 'Clear filters',
      emptyMessage: 'No data for the period.',
    },
    customersByPlanChart: { title: 'Customers by plan', tooltipCustomers: 'Customers' },
    revenueByPlanChart: { title: 'Revenue by plan' },
    signupsTimeseriesChart: { title: 'Signups over the period', tooltipSignups: 'Signups' },
    metricCards: {
      mrr: 'MRR',
      mrrHint: (currency) => `currency ${currency}`,
      churn30d: '30d churn',
      activeUsers24h: 'Active users 24h',
      activeDevices24h: 'Active devices 24h',
      blockedOrgs: 'Blocked companies',
      blockedOrgsHint: (total) => `of ${total}`,
      signups7d: 'Signups 7d',
      signups30d: 'Signups 30d',
    },
    moderationTab: {
      queueDepth: 'Pending queue',
      decisionP50: 'p50 decision',
      decisionP50Hint: 'median',
      decisionP95: 'p95 decision',
      decisionP95Hint: (target) => `vs target ${target}h`,
      aboveSla: (target) => `Above SLA (${target}h)`,
      aboveSlaHint: 'pending + decided',
      approved: 'Approved',
      rejected: 'Rejected',
      fastpath: 'Fastpath',
      autoRejectCap: 'Auto-reject (cap)',
    },
    planFilter: {
      label: 'Plan',
      resultCount: (filtered, total) => `${filtered} of ${total} plans`,
    },
    signupsFilters: {
      periodAriaLabel: 'Period',
      grainAriaLabel: 'Granularity',
      preset7d: '7 days',
      preset30d: '30 days',
      preset90d: '90 days',
      preset1y: '1 year',
      grainDay: 'Daily',
      grainWeek: 'Weekly',
      grainMonth: 'Monthly',
    },
  },
};

export const useMetricsWidgetsContent = (): MetricsWidgetsContent =>
  useBilingual(METRICS_WIDGETS_CONTENT);
